/**
 * Figures shown to people (DESIGN.md §4, DES-19). One place decides how a
 * rate, a trend and a missing value read, so no screen shows "+100 %" from
 * zero or "0 %" for a metric that could not be computed.
 *
 * - A zero denominator gives null, shown as "—".
 * - No previous period gives no trend (null).
 * - A rate's trend is in percentage points; a count's trend in relative %.
 */

/** Meetings over connected calls (CallerStats formula, CEO-7). Null when nothing connected. */
export function conversionRate(meetings: number, connected: number): number | null {
  return connected > 0 ? meetings / connected : null;
}

/** Connected calls over calls (« taux de contact », portal home W5). Null when nothing was called. */
export function contactRate(connected: number, calls: number): number | null {
  return calls > 0 ? connected / calls : null;
}

/** "42 %" (locale-aware), or "—" when there is no denominator. */
export function formatRate(rate: number | null | undefined, locale?: string): string {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) return '—';
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: rate < 0.1 ? 1 : 0 }).format(rate);
}

export interface Trend {
  value: number;
  unit: 'pts' | '%';
  direction: 'up' | 'down' | 'flat';
}

/**
 * Change against the previous period. Null without a previous period, and
 * for a count whose previous value was 0 (a relative change from zero means
 * nothing).
 */
export function trend(current: number | null | undefined, previous: number | null | undefined, kind: 'rate' | 'count'): Trend | null {
  if (current === null || current === undefined || previous === null || previous === undefined) return null;
  let value: number;
  let unit: Trend['unit'];
  if (kind === 'rate') {
    value = Math.round((current - previous) * 1000) / 10;
    unit = 'pts';
  } else {
    if (previous === 0) return null;
    value = Math.round(((current - previous) / previous) * 1000) / 10;
    unit = '%';
  }
  return { value, unit, direction: value > 0 ? 'up' : value < 0 ? 'down' : 'flat' };
}

export function formatTrend(t: Trend | null, locale?: string): string | null {
  if (!t) return null;
  const n = new Intl.NumberFormat(locale, { maximumFractionDigits: 1, signDisplay: 'exceptZero' }).format(t.value);
  return t.unit === 'pts' ? `${n} pts` : `${n} %`;
}
